import React, { useState, useEffect, useCallback } from "react";
import { Calendar, Clock, MapPin, RefreshCw, ChevronLeft, ChevronRight, Star, Filter } from "lucide-react";

interface ProgrammeEvent {
  id: string;
  title: string;
  category: string;
  startTime: string;
  endTime: string;
  venue: string;
  area?: string;
  description?: string;
  speaker?: string;
  dresscode?: string;
  isHighlight?: boolean;
}

interface ProgrammeDay {
  id: string;
  date: string;
  dayLabel: string;
  theme: string;
  events: ProgrammeEvent[];
}

const CAT_COLORS: Record<string, string> = {
  "Opening Ceremony": "#C9A84C",
  "Cultural Display": "#a78bfa",
  "Sports": "#60a5fa",
  "Religious": "#34d399",
  "Entertainment": "#fb923c",
  "Awards": "#C9A84C",
  "Parade": "#2dd4bf",
  "Community Meeting": "#94a3b8",
  "Gala/Dinner": "#f472b6",
  "Youth Event": "#4ade80",
  "Children Event": "#fbbf24",
  "Security Briefing": "#ef4444",
  "Other": "#94a3b8"
};

const SHORT_DAYS = ["Thu","Fri","Sat","Sun","Mon","Tue","Wed"];
const DATES = ["Dec 24","Dec 25","Dec 26","Dec 27","Dec 28","Dec 29","Dec 30"];

export function FestivalCalendar({ key: _k }: { key?: React.Key }) {
  const [days, setDays] = useState<ProgrammeDay[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const [selectedEvt, setSelectedEvt] = useState<ProgrammeEvent | null>(null);
  const [filterCat, setFilterCat] = useState("All");
  const [view, setView] = useState<"week"|"day">("week");

  const loadProgramme = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const r = await fetch("/api/programme");
      const d = await r.json();
      const arr: ProgrammeDay[] = Array.isArray(d) ? d : (Array.isArray(d.days) ? d.days : (Array.isArray(d.programme) ? d.programme : []));
      arr.forEach(day => {
        if (!Array.isArray(day.events)) day.events = [];
        day.events = day.events.map((e: any) => ({
          ...e,
          startTime: e.startTime || e.time     || '',
          endTime:   e.endTime   || '',
          venue:     e.venue     || e.location  || '',
          category:  e.category  || e.type      || 'Other',
        }));
        day.events.sort((a: any, b: any) => (a.startTime || "").localeCompare(b.startTime || ""));
      });
      setDays(arr);
    } catch { setError("Failed to load. Please refresh."); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadProgramme(); }, [loadProgramme]);

  const allCategories = Array.from(new Set(days.flatMap(d => d.events.map(e => e.category)))).sort();
  const totalEvents = days.reduce((s, d) => s + (d.events?.length || 0), 0);
  const highlightCount = days.reduce((s, d) => s + d.events.filter(e => e.isHighlight).length, 0);

  const getFilteredEvents = (dayEvents: ProgrammeEvent[]) =>
    filterCat === "All" ? dayEvents : dayEvents.filter(e => e.category === filterCat);

  const HOURS = Array.from({length:17}, (_,i) => i + 6); // 06:00 to 22:00

  const timeToMinutes = (t: string) => {
    if (!t) return -1;
    const [h, m] = t.split(":").map(Number);
    return h * 60 + (m || 0);
  };

  const currentDayData = selectedDay !== null ? days[selectedDay] : null;

  return (
    <div style={{background:"#070D0A",minHeight:"100vh",padding:"24px",fontFamily:"Plus Jakarta Sans, sans-serif"}}>
      {/* Header */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:12,marginBottom:24}}>
        <div>
          <h1 style={{fontFamily:"Cinzel,serif",color:"#C9A84C",fontSize:22,margin:0}}>
            Festival Calendar
          </h1>
          <p style={{color:"rgba(237,232,220,0.5)",fontSize:13,margin:"4px 0 0"}}>
            ERUE Festival — December 24–30, 2026 &nbsp;|&nbsp;
            {totalEvents} events &nbsp;·&nbsp; {highlightCount} highlights
          </p>
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
          <select value={filterCat} onChange={e => setFilterCat(e.target.value)} style={{padding:"8px 12px",background:"#0E1812",border:"1px solid #1C2E22",borderRadius:8,color:"#EDE8DC",fontSize:13,outline:"none"}}>
            <option value="All">All Categories</option>
            {allCategories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <div style={{display:"flex",border:"1px solid #1C2E22",borderRadius:8,overflow:"hidden"}}>
            <button onClick={() => {setView("week");setSelectedDay(null);}} style={{padding:"8px 16px",background:view==="week"?"#0F5132":"#0E1812",border:"none",color:view==="week"?"#C9A84C":"rgba(237,232,220,0.6)",cursor:"pointer",fontSize:13}}>Week</button>
            <button onClick={() => {setView("day");if(selectedDay===null)setSelectedDay(0);}} style={{padding:"8px 16px",background:view==="day"?"#0F5132":"#0E1812",border:"none",color:view==="day"?"#C9A84C":"rgba(237,232,220,0.6)",cursor:"pointer",fontSize:13}}>Day</button>
          </div>
          <button onClick={loadProgramme} style={{padding:"8px 12px",background:"#0E1812",border:"1px solid #1C2E22",borderRadius:8,color:"rgba(237,232,220,0.6)",cursor:"pointer",display:"flex",alignItems:"center",gap:6,fontSize:13}}>
            <RefreshCw size={14}/> Refresh
          </button>
        </div>
      </div>

      {/* Legend */}
      <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:20}}>
        {Object.entries(CAT_COLORS).slice(0,8).map(([cat, color]) => (
          <span key={cat} style={{display:"flex",alignItems:"center",gap:4,fontSize:11,color:"rgba(237,232,220,0.6)"}}>
            <span style={{width:10,height:10,borderRadius:2,background:color,display:"inline-block"}}/>
            {cat}
          </span>
        ))}
      </div>

      {error && (
        <div style={{background:"#1a0a0a",border:"1px solid #ef4444",borderRadius:8,padding:"10px 16px",marginBottom:16,color:"#ef4444",fontSize:13}}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{textAlign:"center",padding:80,color:"rgba(237,232,220,0.4)"}}>
          <Calendar size={40} style={{margin:"0 auto 12px",opacity:0.3}}/>
          <p>Loading calendar...</p>
        </div>
      ) : totalEvents === 0 ? (
        <div style={{textAlign:"center",padding:80,color:"rgba(237,232,220,0.4)"}}>
          <Calendar size={48} style={{margin:"0 auto 16px",opacity:0.2}}/>
          <p style={{fontSize:18,fontFamily:"Cinzel,serif",color:"rgba(237,232,220,0.3)"}}>Programme Coming Soon</p>
          <p style={{fontSize:14}}>Events will appear here once the schedule is published by the organising committee.</p>
        </div>
      ) : (
        <>
          {/* Week View */}
          {view === "week" && (
            <div style={{overflowX:"auto"}}>
              {/* Day Headers */}
              <div style={{display:"grid",gridTemplateColumns:`60px repeat(7, 1fr)`,gap:2,marginBottom:2}}>
                <div/>
                {days.map((day, i) => {
                  const evts = getFilteredEvents(day.events);
                  const hasHigh = evts.some(e => e.isHighlight);
                  return (
                    <div key={day.id} onClick={() => {setSelectedDay(i);setView("day");}}
                      style={{background:"#0E1812",border:`1px solid ${selectedDay===i?"#C9A84C":"#1C2E22"}`,borderRadius:8,padding:"10px 8px",textAlign:"center",cursor:"pointer",transition:"all 0.2s"}}>
                      <div style={{color:"#C9A84C",fontSize:12,fontWeight:700,fontFamily:"Cinzel,serif"}}>{SHORT_DAYS[i]}</div>
                      <div style={{color:"#EDE8DC",fontSize:16,fontWeight:700}}>{DATES[i].split(" ")[1]}</div>
                      <div style={{color:"rgba(237,232,220,0.5)",fontSize:11}}>{evts.length} event{evts.length!==1?"s":""}</div>
                      {hasHigh && <div style={{color:"#C9A84C",fontSize:12}}>★</div>}
                    </div>
                  );
                })}
              </div>

              {/* Time Grid */}
              {HOURS.map(hour => (
                <div key={hour} style={{display:"grid",gridTemplateColumns:`60px repeat(7, 1fr)`,gap:2,marginBottom:2}}>
                  <div style={{display:"flex",alignItems:"flex-start",justifyContent:"flex-end",paddingRight:8,paddingTop:4}}>
                    <span style={{color:"rgba(237,232,220,0.35)",fontSize:11,fontWeight:600}}>{String(hour).padStart(2,"0")}:00</span>
                  </div>
                  {days.map((day, di) => {
                    const evts = getFilteredEvents(day.events).filter(e => {
                      const sm = timeToMinutes(e.startTime);
                      return sm >= hour * 60 && sm < (hour + 1) * 60;
                    });
                    return (
                      <div key={day.id} style={{background:"#0E1812",borderRadius:4,minHeight:40,padding:2,border:"1px solid #0E1812"}}>
                        {evts.map(evt => {
                          const color = CAT_COLORS[evt.category] || "#94a3b8";
                          return (
                            <div key={evt.id} onClick={() => setSelectedEvt(evt)}
                              style={{background:color+"22",border:`1px solid ${color}55`,borderRadius:4,padding:"3px 6px",marginBottom:2,cursor:"pointer",borderLeft:`3px solid ${color}`}}>
                              <div style={{color:"#EDE8DC",fontSize:11,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                                {evt.isHighlight && "★ "}{evt.title}
                              </div>
                              <div style={{color:"rgba(237,232,220,0.5)",fontSize:10}}>{evt.startTime}{evt.endTime?`–${evt.endTime}`:""}</div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}

          {/* Day View */}
          {view === "day" && currentDayData && (
            <div>
              {/* Day Nav */}
              <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:20}}>
                <button onClick={() => setSelectedDay(Math.max(0,(selectedDay||0)-1))} disabled={(selectedDay||0)===0}
                  style={{padding:"8px",background:"#0E1812",border:"1px solid #1C2E22",borderRadius:8,color:"rgba(237,232,220,0.7)",cursor:"pointer"}}>
                  <ChevronLeft size={16}/>
                </button>
                <div style={{flex:1,textAlign:"center"}}>
                  <h2 style={{color:"#EDE8DC",fontFamily:"Cinzel,serif",margin:0,fontSize:18}}>{currentDayData.dayLabel}</h2>
                  <p style={{color:"#C9A84C",fontSize:13,margin:"4px 0 0"}}>{currentDayData.theme}</p>
                </div>
                <button onClick={() => setSelectedDay(Math.min(6,(selectedDay||0)+1))} disabled={(selectedDay||0)===6}
                  style={{padding:"8px",background:"#0E1812",border:"1px solid #1C2E22",borderRadius:8,color:"rgba(237,232,220,0.7)",cursor:"pointer"}}>
                  <ChevronRight size={16}/>
                </button>
              </div>

              {/* Day Selector Tabs */}
              <div style={{display:"flex",gap:4,marginBottom:20,overflowX:"auto"}}>
                {days.map((day, i) => (
                  <button key={day.id} onClick={() => setSelectedDay(i)}
                    style={{flexShrink:0,padding:"6px 14px",borderRadius:8,border:`1px solid ${selectedDay===i?"#C9A84C":"#1C2E22"}`,background:selectedDay===i?"#0F5132":"#0E1812",color:selectedDay===i?"#C9A84C":"rgba(237,232,220,0.6)",cursor:"pointer",fontSize:12,fontWeight:600}}>
                    {SHORT_DAYS[i]} {DATES[i].split(" ")[1]}
                  </button>
                ))}
              </div>

              {/* Events Timeline */}
              {getFilteredEvents(currentDayData.events).length === 0 ? (
                <div style={{textAlign:"center",padding:60,color:"rgba(237,232,220,0.4)"}}>
                  <Clock size={32} style={{margin:"0 auto 12px",opacity:0.3}}/>
                  <p>No events{filterCat!=="All"?` in "${filterCat}"`:" scheduled"} for this day</p>
                </div>
              ) : (
                <div style={{display:"flex",flexDirection:"column",gap:10}}>
                  {getFilteredEvents(currentDayData.events).map(evt => {
                    const color = CAT_COLORS[evt.category] || "#94a3b8";
                    return (
                      <div key={evt.id} onClick={() => setSelectedEvt(evt)}
                        style={{background:"#0E1812",border:`1px solid #1C2E22`,borderRadius:12,padding:"16px 20px",cursor:"pointer",borderLeft:`4px solid ${color}`,transition:"border-color 0.2s"}}>
                        <div style={{display:"flex",alignItems:"flex-start",gap:16,flexWrap:"wrap"}}>
                          <div style={{flexShrink:0,minWidth:70,textAlign:"center",background:"#121E16",borderRadius:8,padding:"8px 12px"}}>
                            <div style={{color:"#C9A84C",fontSize:16,fontWeight:700}}>{evt.startTime||"TBD"}</div>
                            {evt.endTime && <div style={{color:"rgba(237,232,220,0.4)",fontSize:12}}>→ {evt.endTime}</div>}
                          </div>
                          <div style={{flex:1}}>
                            <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",marginBottom:6}}>
                              {evt.isHighlight && <span style={{color:"#C9A84C",fontSize:16}}>★</span>}
                              <h3 style={{color:"#EDE8DC",fontSize:16,fontWeight:700,margin:0}}>{evt.title}</h3>
                              <span style={{padding:"3px 10px",borderRadius:12,fontSize:11,fontWeight:600,background:color+"22",color,border:`1px solid ${color}44`}}>{evt.category}</span>
                            </div>
                            {evt.venue && (
                              <p style={{color:"rgba(237,232,220,0.5)",fontSize:13,margin:"0 0 4px",display:"flex",alignItems:"center",gap:4}}>
                                <MapPin size={12}/> {evt.venue}{evt.area?` — ${evt.area}`:""}
                              </p>
                            )}
                            {evt.speaker && <p style={{color:"rgba(237,232,220,0.6)",fontSize:13,margin:"0 0 4px"}}><strong style={{color:"rgba(201,168,76,0.8)"}}>Speaker/Performer:</strong> {evt.speaker}</p>}
                            {evt.dresscode && <p style={{color:"rgba(237,232,220,0.5)",fontSize:12,margin:0}}><strong>Dress Code:</strong> {evt.dresscode}</p>}
                            {evt.description && <p style={{color:"rgba(237,232,220,0.6)",fontSize:13,margin:"8px 0 0",borderTop:"1px solid #1C2E22",paddingTop:8}}>{evt.description}</p>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Event Detail Modal */}
      {selectedEvt && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.85)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:16}} onClick={() => setSelectedEvt(null)}>
          <div style={{background:"#0E1812",border:`1px solid ${CAT_COLORS[selectedEvt.category]||"#C9A84C"}`,borderRadius:16,padding:28,maxWidth:480,width:"100%"}} onClick={e => e.stopPropagation()}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16}}>
              <span style={{padding:"4px 12px",borderRadius:12,fontSize:12,fontWeight:600,background:(CAT_COLORS[selectedEvt.category]||"#94a3b8")+"22",color:CAT_COLORS[selectedEvt.category]||"#94a3b8",border:`1px solid ${CAT_COLORS[selectedEvt.category]||"#94a3b8"}44`}}>
                {selectedEvt.category}
              </span>
              <button onClick={() => setSelectedEvt(null)} style={{background:"none",border:"none",color:"rgba(237,232,220,0.4)",cursor:"pointer",fontSize:20}}>✕</button>
            </div>
            <h2 style={{color:"#EDE8DC",fontFamily:"Cinzel,serif",margin:"0 0 16px",fontSize:20}}>
              {selectedEvt.isHighlight && <span style={{color:"#C9A84C"}}>★ </span>}{selectedEvt.title}
            </h2>
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              {(selectedEvt.startTime||selectedEvt.endTime) && (
                <div style={{display:"flex",alignItems:"center",gap:8,color:"rgba(237,232,220,0.7)",fontSize:14}}>
                  <Clock size={14} style={{color:"#C9A84C"}}/> {selectedEvt.startTime}{selectedEvt.endTime?` – ${selectedEvt.endTime}`:""}
                </div>
              )}
              {selectedEvt.venue && (
                <div style={{display:"flex",alignItems:"center",gap:8,color:"rgba(237,232,220,0.7)",fontSize:14}}>
                  <MapPin size={14} style={{color:"#C9A84C"}}/> {selectedEvt.venue}{selectedEvt.area?` — ${selectedEvt.area}`:""}
                </div>
              )}
              {selectedEvt.speaker && <p style={{color:"rgba(237,232,220,0.7)",fontSize:14,margin:0}}><strong style={{color:"#C9A84C"}}>Speaker/Performer:</strong> {selectedEvt.speaker}</p>}
              {selectedEvt.dresscode && <p style={{color:"rgba(237,232,220,0.7)",fontSize:14,margin:0}}><strong style={{color:"#C9A84C"}}>Dress Code:</strong> {selectedEvt.dresscode}</p>}
              {selectedEvt.description && <p style={{color:"rgba(237,232,220,0.6)",fontSize:14,margin:"8px 0 0",padding:"12px",background:"#121E16",borderRadius:8,lineHeight:1.6}}>{selectedEvt.description}</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FestivalCalendar;
